import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Button, Drawer, Space, Spin, Tag, Timeline } from "antd";
import { useEffect, useState } from "react";
import {
  getLegacyRevisions,
  loadLegacyContentState,
  publishLegacyContent,
  subscribeLegacyState,
  type LegacyContentState,
} from "@innovate/cms-admin-ui";
import type { CmsAdminUser } from "@innovate/cms-contract";
import { InnovateAdminDashboard } from "../innovate/InnovateAdminDashboard";

export function LegacyAdminApp({ user }: { user: CmsAdminUser }) {
  const { message } = App.useApp();
  const initialState = useQuery({
    queryKey: ["legacy-admin-content"],
    queryFn: loadLegacyContentState,
    staleTime: Infinity,
  });
  const [state, setState] = useState<LegacyContentState | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const revisions = useQuery({
    queryKey: ["legacy-admin-revisions"],
    queryFn: getLegacyRevisions,
    enabled: versionsOpen,
  });
  const publish = useMutation({
    mutationFn: publishLegacyContent,
    onSuccess: (next) => {
      setState(next);
      message.success("内容已发布");
    },
    onError: (error) => message.error(error.message),
  });

  useEffect(() => subscribeLegacyState(setState), []);
  useEffect(() => {
    if (initialState.data) setState(initialState.data);
  }, [initialState.data]);

  if (initialState.error) {
    return <Alert type="error" showIcon message={initialState.error.message} />;
  }
  if (initialState.isLoading || !state) {
    return <div className="grid min-h-screen place-items-center"><Spin size="large" /></div>;
  }

  return (
    <>
      <InnovateAdminDashboard
        content={state.content}
        user={user}
        publishedAt={state.published_at}
        hasUnpublished={state.has_unpublished}
        publishing={publish.isPending}
        onOpenVersions={() => setVersionsOpen(true)}
        onPublish={async () => {
          await publish.mutateAsync();
        }}
      />
      <Drawer title="版本记录" width={520} open={versionsOpen} onClose={() => setVersionsOpen(false)}>
        {revisions.isLoading ? <Spin /> : (
          <Timeline items={(revisions.data?.results || []).map((revision) => ({
            color: "green",
            children: (
              <div>
                <Space wrap>
                  <Tag>v{revision.version}</Tag>
                  <Tag color="green">已发布</Tag>
                </Space>
                <p>发布 {revision.document_count} 项内容</p>
                <small>{revision.author} · {new Date(revision.published_at).toLocaleString("zh-CN", { hour12: false })}</small>
              </div>
            ),
          }))} />
        )}
        <Button onClick={() => revisions.refetch()}>刷新</Button>
      </Drawer>
    </>
  );
}
